import { useEffect, useState } from "react";
import Reveal from "@/components/torq/Reveal";
import StationModal from "@/components/torq/StationModal";
import { STATION_FALLBACKS, RUN_DEFAULTS, DIVISION_COLUMNS } from "@/components/torq/stationData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chip = ({ children, run, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center font-mono text-[10px] tracking-[0.15em] px-3 py-2 border whitespace-nowrap ${
      run ? "border-[#f0eee4] text-[#f0eee4]" : "border-[#454540] text-[#a3a39f] bg-[#1e1e1c]"
    }`}
  >
    {children}
  </span>
);

const Arrow = () => <span className="font-mono text-[#454540] text-xs" aria-hidden="true">→</span>;

export default function Format() {
  const [ev, setEv] = useState(null);
  const [openStation, setOpenStation] = useState(null);

  useEffect(() => {
    fetch(`${API}/event`)
      .then((r) => r.json())
      .then(setEv)
      .catch(() => {});
  }, []);

  const stations = ev?.stations?.length ? ev.stations : STATION_FALLBACKS;
  const legM = ev?.run_leg_meters || RUN_DEFAULTS.run_leg_meters;
  const legs = ev?.run_legs || RUN_DEFAULTS.run_legs;
  const totalKm = ((legM * legs) / 1000).toFixed(1);
  const provisional = (ev?.course_status || RUN_DEFAULTS.course_status) === "provisional";

  // 8 running legs with the 7 stations slotted between them
  const sequence = [];
  for (let leg = 1; leg <= legs; leg++) {
    sequence.push({ type: "run", label: leg === legs ? `FINAL ${legM}M RUN` : `${legM}M RUN`, key: `run-${leg}`, final: leg === legs });
    if (leg <= stations.length) {
      const st = stations[leg - 1];
      sequence.push({ type: "station", label: `S${leg} — ${st.name}`, key: `st-${st.id || leg}`, station: st });
    }
  }

  return (
    <section id="format" className="border-b border-[#2e2e2a] py-24 lg:py-32" data-testid="format-section">
      <div className="px-6 lg:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] text-[#ff5a1f] mb-10" data-testid="format-overline">
            02 / THE FORMAT
          </p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-end">
          <Reveal delay={0.05}>
            <h2
              className="font-display font-extrabold uppercase leading-[0.9] text-[clamp(2.5rem,6vw,5.5rem)]"
              data-testid="format-heading"
            >
              EIGHT LEGS. <span className="text-stroke">SEVEN STATIONS.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="max-w-xl text-[#a3a39f] leading-relaxed text-base lg:text-lg" data-testid="format-copy">
              Run {legM} metres. Hit the station. Repeat — {legs} running legs and {stations.length} workout
              stations, {totalKm} km of running in all. After Station {stations.length}, the final {legM} m
              takes you straight to the finish line, where the clock stops. One arena. One clock.
            </p>
            {provisional ? (
              <p className="mt-4 max-w-xl font-mono text-[10px] tracking-[0.15em] text-[#d8a24a] leading-relaxed" data-testid="format-provisional-note">
                PROVISIONAL — course distance, station weights, reps and standards are subject to final organiser confirmation and course measurement.
              </p>
            ) : (
              <p className="mt-4 max-w-xl font-mono text-[10px] tracking-[0.15em] text-[#ff5a1f] leading-relaxed" data-testid="format-confirmed-note">
                OFFICIAL RACE STANDARDS — station weights, reps and distances confirmed by the organiser. Doubles totals are shared between partners.
              </p>
            )}
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 border border-[#2e2e2a] p-5 lg:p-8" data-testid="race-sequence">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-5">THE SEQUENCE — START TO FINISH</p>
            <div className="flex flex-wrap items-center gap-2">
              <Chip run testId="seq-start">START</Chip>
              {sequence.map((item) => (
                <span key={item.key} className="inline-flex items-center gap-2">
                  <Arrow />
                  {item.type === "run" ? (
                    <Chip run testId={`seq-${item.key}`}>{item.label}</Chip>
                  ) : (
                    <button
                      onClick={() => setOpenStation(item.station)}
                      data-testid={`seq-${item.key}`}
                      className="inline-flex items-center font-mono text-[10px] tracking-[0.15em] px-3 py-2 border border-[#454540] text-[#a3a39f] bg-[#1e1e1c] hover:border-[#f0eee4] hover:text-[#f0eee4] transition-colors whitespace-nowrap"
                    >
                      {item.label}
                    </button>
                  )}
                </span>
              ))}
              <Arrow />
              <Chip run testId="seq-finish">FINISH LINE</Chip>
            </div>
            <div className="mt-6 pt-5 border-t border-[#2e2e2a] flex flex-wrap gap-x-8 gap-y-2 font-mono text-[10px] tracking-[0.2em] text-[#6e6e6a]" data-testid="race-metrics">
              <span>TOTAL RUNNING — {totalKm} KM</span>
              <span>RUNNING LEGS — {legs} × {legM}M</span>
              <span>WORKOUT STATIONS — {stations.length}</span>
              <span>FINAL RUN COUNTS — NO 8TH STATION</span>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-14 border-t border-[#2e2e2a]" data-testid="format-station-list">
        {stations.map((s, i) => (
          <Reveal key={s.id || s.n} delay={i * 0.03} y={20}>
            <button
              onClick={() => setOpenStation(s)}
              data-testid={`station-row-${(s.id || s.name.toLowerCase()).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group relative w-full flex items-center justify-between gap-6 px-6 lg:px-12 py-6 lg:py-8 border-b border-[#2e2e2a] transition-colors duration-300 hover:bg-[#f0eee4] hover:text-[#161614] text-left"
              aria-haspopup="dialog"
            >
              <div className="flex items-baseline gap-5 lg:gap-10 min-w-0">
                <span className="font-mono text-xs text-[#6e6e6a] group-hover:text-[#161614]/60 transition-colors">
                  {s.n}
                </span>
                <span className="font-display font-extrabold uppercase text-2xl sm:text-4xl lg:text-6xl leading-none tracking-wide truncate">
                  {s.name}
                </span>
              </div>
              <span className="flex items-center gap-4 shrink-0">
                <span className="hidden sm:block font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] group-hover:text-[#161614]/60 transition-colors whitespace-nowrap">
                  {s.tag}
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] border border-[#454540] group-hover:border-[#161614]/40 px-3 py-1.5 whitespace-nowrap">
                  VIEW DETAILS →
                </span>
              </span>
            </button>
          </Reveal>
        ))}
      </div>

      {openStation && <StationModal station={openStation} onClose={() => setOpenStation(null)} />}

      {stations.some((s) => s.standards) && (
        <div className="px-6 lg:px-12 mt-16" data-testid="standards-table-section">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] text-[#ff5a1f] mb-6">STATION STANDARDS</p>
            <div className="overflow-x-auto border border-[#2e2e2a]">
              <table className="w-full min-w-[760px] text-left" data-testid="standards-table">
                <thead>
                  <tr className="border-b border-[#2e2e2a] font-mono text-[10px] tracking-[0.15em] text-[#6e6e6a]">
                    <th className="px-4 py-4 font-normal">STATION</th>
                    {DIVISION_COLUMNS.map(([k, lbl]) => (
                      <th key={k} className="px-4 py-4 font-normal">{lbl}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stations.map((s) => (
                    <tr key={s.id || s.n} className="border-b border-[#2e2e2a] last:border-0 hover:bg-[#1e1e1c] transition-colors" data-testid={`standards-row-${s.id}`}>
                      <td className="px-4 py-4 font-display font-bold uppercase text-sm whitespace-nowrap">
                        <span className="font-mono text-[10px] text-[#6e6e6a] mr-2">{s.n}</span>{s.name}
                      </td>
                      {DIVISION_COLUMNS.map(([k]) => (
                        <td key={k} className="px-4 py-4 font-mono text-[11px] tracking-[0.05em] text-[#a3a39f]">
                          {s.standards?.[k] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-[0.12em] text-[#6e6e6a] leading-relaxed">
              DOUBLES TOTALS ARE SHARED BETWEEN PARTNERS. JUDGES VERIFY EVERY STANDARD AT THE STATION.
            </p>
            <a
              href={`${API}/rulebook.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="rulebook-download"
              className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] border border-[#ff5a1f] text-[#ff5a1f] px-6 py-3.5 min-h-[48px] hover:bg-[#ff5a1f] hover:text-[#0c0c0a] transition-colors"
            >
              DOWNLOAD ATHLETE RULEBOOK (PDF)
            </a>
          </Reveal>
        </div>
      )}
    </section>
  );
}
